import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomizedSelectoption from "../General/CustomizedSelectoption";
import { Ask_Question, Show_Message } from "../General/Messagedialogbox";
import { API_FETCH, API_POST } from "../General/Utility";
import { Is_ValidResponse, Parse_Json, To_DateInput } from "./ReportTable";

// Same rule as the server (rbsBALReportFilter.fnNormalizeDataType): case and tabs ignored
const Normalize_Type = (strType) => {
  const t = String(strType || "").replace(/\t/g, "").trim().toLowerCase();
  if (t === "datetime" || t === "date") return "DateTime";
  if (t === "decimal") return "decimal";
  return "String";
};
const BETWEEN_ID = 6;
const SEARCH_DELAY = 300; // ms: typed value search is debounced; dropdown / date changes are instant
const Has_Key = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
const Is_Amount = (v) => /^\s*-?(\d+(\.\d*)?|\.\d+)\s*$/.test(String(v === null || v === undefined ? "" : v));
// Same limits as the server (rbsBALReportFilter.fnValidateAmount). 10 integer digits + 5 decimals = 15 significant
// digits: the most a JavaScript number carries EXACTLY, so the server receives precisely what was typed.
const AMOUNT_DECIMALS = 5;
const Amount_Error = (v, strName) => {
  if (!Is_Amount(v)) return "Please enter a valid " + strName.toLowerCase();
  const strVal = String(v).trim();
  const iDot = strVal.indexOf(".");
  if (iDot >= 0 && strVal.length - iDot - 1 > AMOUNT_DECIMALS) return strName + " can have at most " + AMOUNT_DECIMALS + " decimal places";
  const strInt = (iDot >= 0 ? strVal.slice(0, iDot) : strVal).replace("-", "").replace(/^0+/, "");
  if (strInt.length > 10) return strName + " must be between -9,999,999,999.99999 and 9,999,999,999.99999";
  return "";
};

//#region Date range shortcuts (plain ES2015, local calendar dates)
const Pad2 = (n) => (n < 10 ? "0" : "") + n;
const Iso = (d) => d.getFullYear() + "-" + Pad2(d.getMonth() + 1) + "-" + Pad2(d.getDate());
const Add_Days = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
// Financial year containing dtRef; FY start from General Settings (default 1-April)
const FinYear_Range = (strFinFrom, dtRef, iShift) => {
  const strStart = To_DateInput(strFinFrom);
  const iMonth = strStart ? Number(strStart.slice(5, 7)) - 1 : 3;
  const iDay = strStart ? Number(strStart.slice(8, 10)) : 1;
  let iYear = dtRef.getFullYear();
  if (dtRef < new Date(iYear, iMonth, iDay)) iYear -= 1;
  iYear += iShift;
  return [Iso(new Date(iYear, iMonth, iDay)), Iso(Add_Days(new Date(iYear + 1, iMonth, iDay), -1))];
};
const Date_Presets = (strFinFrom) => {
  const t = new Date();
  const dtToday = new Date(t.getFullYear(), t.getMonth(), t.getDate());
  const iDow = (dtToday.getDay() + 6) % 7; // Monday = 0
  const iQ = Math.floor(dtToday.getMonth() / 3) * 3; // calendar quarters = Indian FY quarter boundaries
  const iY = dtToday.getFullYear();
  const iM = dtToday.getMonth();
  return [
    { key: "custom", label: "Custom Range", range: null },
    { key: "today", label: "Today", range: [Iso(dtToday), Iso(dtToday)] },
    { key: "yesterday", label: "Yesterday", range: [Iso(Add_Days(dtToday, -1)), Iso(Add_Days(dtToday, -1))] },
    { key: "week", label: "This Week", range: [Iso(Add_Days(dtToday, -iDow)), Iso(Add_Days(dtToday, 6 - iDow))] },
    { key: "last7", label: "Last 7 Days", range: [Iso(Add_Days(dtToday, -6)), Iso(dtToday)] },
    { key: "last30", label: "Last 30 Days", range: [Iso(Add_Days(dtToday, -29)), Iso(dtToday)] },
    { key: "month", label: "This Month", range: [Iso(new Date(iY, iM, 1)), Iso(new Date(iY, iM + 1, 0))] },
    { key: "lastmonth", label: "Last Month", range: [Iso(new Date(iY, iM - 1, 1)), Iso(new Date(iY, iM, 0))] },
    { key: "quarter", label: "This Quarter", range: [Iso(new Date(iY, iQ, 1)), Iso(new Date(iY, iQ + 3, 0))] },
    { key: "fy", label: "This Financial Year", range: FinYear_Range(strFinFrom, dtToday, 0) },
    { key: "lastfy", label: "Last Financial Year", range: FinYear_Range(strFinFrom, dtToday, -1) },
  ];
};
//#endregion

function ReportFilter(props) {
  const dispatch = useDispatch();
  const _selecrparem = useSelector((state) => state.SelectOption.parems);
  const _AuthParems = useSelector((state) => state.Common.Admin);
  const [strMaxValue, setstrMaxValue] = useState("");
  const [strMinValue, setstrMinValue] = useState("");
  const [strFromDate, setstrFromDate] = useState("");
  const [strToDate, setstrToDate] = useState("");
  const [iRptColId, setiRptColId] = useState(0);
  const [strFieldType, setstrFieldType] = useState("");
  const [MathSymbol, setMathSymbol] = useState(1);
  const [isDate, setisDate] = useState(false);
  const [isAmt, setisAmt] = useState(false);
  const [isTable, setisTable] = useState(false);
  const [isMinMaxVal, setisMinMaxVal] = useState(false);
  const [strSearch, setstrSearch] = useState("");
  const [strLoadedSearch, setstrLoadedSearch] = useState(""); // search the value list was loaded with
  const [IsSaving, setIsSaving] = useState(false);
  const [IsLoading, setIsLoading] = useState(false);
  const [arrRptCol, setarrRptCol] = useState([]);
  const [arrFiltCol, setarrFiltCol] = useState([]);
  const [arrRptData, setarrRptData] = useState([]);
  const [arrMathSymbol, setarrMathSymbol] = useState([]);
  const [arrActive, setarrActive] = useState([]); // saved filters of this report (server: EntActive)
  const [IsPerUser, setIsPerUser] = useState(false); // server: filters saved per user (true) or shared (false)
  const [objEdits, setobjEdits] = useState({}); // value Id -> checked; only changes not saved yet
  const [objSaved, setobjSaved] = useState(null); // saved date / amount of the current column

  const IsMounted = useRef(true);
  const LoadSeq = useRef(0);
  const ColSeq = useRef(0);
  const BusyRef = useRef(false);
  const SearchTimer = useRef(null);
  const CurrentRef = useRef({ iColId: 0, strType: "" });
  // latest callbacks: a save that completes after the popup was closed still refreshes the report
  const AppliedRef = useRef(props.Applied);
  const CloseRef = useRef(props.Close);
  AppliedRef.current = props.Applied;
  CloseRef.current = props.Close;
  const BranchId = Number(props.iBranchId) || Number(_AuthParems.Branch);
  const WHId = Number(props.iWHId) || 0;
  const UserId = Number(_AuthParems.User) || 0;
  // filters are saved per user: every filter call says which user it is for (the server checks report access)
  const UserQs = `?iUserId=${UserId}`;

  useEffect(() => {
    IsMounted.current = true;
    Load_ReportFilter(0);
    const On_Key = (e) => {
      if (e.key === "Escape" || e.key === "Esc") CloseRef.current(false);
    };
    document.addEventListener("keydown", On_Key);
    return () => {
      IsMounted.current = false;
      clearTimeout(SearchTimer.current);
      document.removeEventListener("keydown", On_Key);
    };
  }, []);

  //#region Load
  const Load_ReportFilter = async (iKeepColId) => {
    const iSeq = ++ColSeq.current;
    try {
      const res = await API_FETCH(
        `Reports/Load_ReportFilter/${Number(props.iRptId)}/${BranchId}/${_AuthParems.DB}${UserQs}`,
        dispatch
      );
      if (!IsMounted.current || iSeq !== ColSeq.current || !Is_ValidResponse(res, dispatch)) return;
      const FilderList = (Array.isArray(res.EntFilter) ? res.EntFilter : []).map((Data) => ({
        ...Data,
        iRptColId: Number(Data.iRptColId),
        strDataType: Normalize_Type(Data.strDataType),
      }));
      const arrSaved = Array.isArray(res.EntActive) ? res.EntActive : [];
      setarrRptCol(FilderList);
      setarrMathSymbol(Array.isArray(res.EntMathSymbol) ? res.EntMathSymbol : []);
      setarrActive(arrSaved);
      setIsPerUser(res.IsPerUserFilters === true);
      if (FilderList.length === 0) {
        Clear_defaultvalues();
        setisTable(false);
        setisDate(false);
        setisAmt(false);
        setiRptColId(0);
        Show_Message(dispatch, "No filter columns are set for this report (Report Setting > Report Filter)", "info");
        return;
      }
      // keep the current column; else the first column that has a saved filter; else the first column
      let objCurrent = FilderList.find((dt) => dt.iRptColId === Number(iKeepColId));
      if (!objCurrent) objCurrent = FilderList.find((dt) => arrSaved.some((a) => Number(a.iRptColId) === dt.iRptColId));
      if (!objCurrent) objCurrent = FilderList[0];
      Open_Column(objCurrent.iRptColId, objCurrent.strDataType);
    } catch (e) {
      if (IsMounted.current) Show_Message(dispatch, e.message, "error");
    }
  };
  const Clear_defaultvalues = () => {
    setarrRptData([]);
    setarrFiltCol([]);
    setobjEdits({});
    setobjSaved(null);
    setstrMaxValue("");
    setstrMinValue("");
    setMathSymbol(1);
    setisMinMaxVal(false);
    setstrFromDate("");
    setstrToDate("");
  };
  const Open_Column = (Colid, strColype) => {
    clearTimeout(SearchTimer.current);
    const strType = Normalize_Type(strColype);
    CurrentRef.current = { iColId: Number(Colid), strType: strType };
    Clear_defaultvalues();
    setstrSearch("");
    setstrLoadedSearch("");
    setiRptColId(Number(Colid));
    setstrFieldType(strType);
    setisTable(strType === "String");
    setisDate(strType === "DateTime");
    setisAmt(strType === "decimal");
    Load_FilterData(Colid, strType, "");
  };
  // saved state + value list of one column; the latest request wins (column / search changes)
  const Load_FilterData = async (Colid, strType, strSearchText) => {
    const iSeq = ++LoadSeq.current;
    setIsLoading(true);
    try {
      const parems = {
        strColName: strType,
        strSearch: strType === "String" ? strSearchText || "" : "",
        iItemId: Number(Colid),
        iRptId: Number(props.iRptId),
        iUserId: UserId,
        iBranchId: BranchId,
        iWHId: WHId,
        strDBName: _AuthParems.DB,
      };
      const res = await API_POST(`Reports/Load_ReportFilterCol/`, parems, dispatch);
      if (!IsMounted.current || iSeq !== LoadSeq.current) return; // another column / search meanwhile
      if (!Is_ValidResponse(res, dispatch)) return;
      switch (strType) {
        case "decimal": {
          const objFil = (Array.isArray(res.EntFilterCol) ? res.EntFilterCol : [])[0] || null;
          const iMSId = objFil && Number(objFil.msId) > 0 ? Number(objFil.msId) : 1;
          const strMin = objFil && objFil.minValue !== null && objFil.minValue !== undefined ? String(objFil.minValue) : "";
          const strMax = objFil && iMSId === BETWEEN_ID && objFil.maxValue !== null && objFil.maxValue !== undefined ? String(objFil.maxValue) : "";
          setMathSymbol(iMSId);
          setisMinMaxVal(iMSId === BETWEEN_ID);
          setstrMinValue(strMin);
          setstrMaxValue(strMax);
          setobjSaved({ iMSId: iMSId, strMin: strMin, strMax: strMax });
          break;
        }
        case "DateTime": {
          const objFil = (Array.isArray(res.EntFilterCol) ? res.EntFilterCol : [])[0] || null;
          const strFrom = objFil ? To_DateInput(objFil.fromDate) : "";
          const strTo = objFil ? To_DateInput(objFil.toDate) : "";
          setstrFromDate(strFrom);
          setstrToDate(strTo);
          setobjSaved({ strFrom: strFrom, strTo: strTo });
          break;
        }
        default: {
          const arrFilter = Parse_Json(res.EntFilterCol).map((dtcol) => ({
            ...dtcol,
            Check: Boolean(Number(dtcol.Check)),
            Id: Number(dtcol.Id),
            SNo: Number(dtcol.SNo),
          }));
          setarrRptData(arrFilter);
          setarrFiltCol(arrFilter.length > 0 ? Object.keys(arrFilter[0]) : []);
          setstrLoadedSearch(strSearchText || "");
          break;
        }
      }
    } catch (e) {
      if (IsMounted.current && iSeq === LoadSeq.current) Show_Message(dispatch, e.message, "error");
    } finally {
      if (IsMounted.current && iSeq === LoadSeq.current) setIsLoading(false);
    }
  };
  //#endregion

  //#region Edit state (unsaved changes survive value searches)
  const Is_Checked = (dt) => (Has_Key(objEdits, dt.Id) ? objEdits[dt.Id] : dt.Check);
  const Set_Checked = (arrItems, Fn_Value) => {
    setobjEdits((objOld) => {
      const objNew = { ...objOld };
      arrItems.forEach((dt) => {
        const bVal = Fn_Value(Has_Key(objOld, dt.Id) ? objOld[dt.Id] : dt.Check);
        if (bVal === dt.Check) delete objNew[dt.Id]; // back to the saved value: not a change
        else objNew[dt.Id] = bVal;
      });
      return objNew;
    });
  };
  const iEditCount = Object.keys(objEdits).length;
  const iCheckedCount = useMemo(
    () => arrRptData.reduce((n, dt) => n + ((Has_Key(objEdits, dt.Id) ? objEdits[dt.Id] : dt.Check) ? 1 : 0), 0),
    [arrRptData, objEdits]
  );
  const IsDirty =
    (strFieldType === "String" && iEditCount > 0) ||
    (strFieldType === "DateTime" && objSaved !== null && (strFromDate !== objSaved.strFrom || strToDate !== objSaved.strTo)) ||
    (strFieldType === "decimal" &&
      objSaved !== null &&
      (Number(MathSymbol) !== objSaved.iMSId ||
        String(strMinValue).trim() !== objSaved.strMin ||
        (isMinMaxVal && String(strMaxValue).trim() !== objSaved.strMax)));
  //#endregion

  //#region Validation (shown before saving; the server checks the same rules)
  const Validate = () => {
    if (!Number(iRptColId)) return "Please select a filter column";
    if (strFieldType === "DateTime") {
      if (!strFromDate || !strToDate) return "Please select From and To dates";
      if (strFromDate > strToDate) return "To date cannot be earlier than From date";
      if (strFromDate < "1900-01-01" || strToDate > "2999-12-31") return "Please select a valid date";
    }
    if (strFieldType === "decimal") {
      const strMinError = Amount_Error(strMinValue, isMinMaxVal ? "Min amount" : "Amount");
      if (strMinError) return strMinError;
      if (isMinMaxVal) {
        const strMaxError = Amount_Error(strMaxValue, "Max amount");
        if (strMaxError) return strMaxError;
        if (Number(strMinValue) > Number(strMaxValue)) return "Min amount cannot be greater than Max amount";
      }
    }
    if (strFieldType === "String") {
      if (iEditCount === 0) return "No values were changed";
      // whole list loaded (no search) and nothing ticked: every row would be filtered out
      if (!strLoadedSearch.trim() && arrRptData.length > 0 && iCheckedCount === 0)
        return "Please select at least one value (excluding every value returns no rows)";
    }
    return "";
  };
  const strValidation = IsDirty ? Validate() : "";
  //#endregion

  //#region Save / delete / reset: one at a time, report refreshed right after each
  const Run_Change = async (fnRequest, iReloadColId) => {
    if (BusyRef.current) return;
    BusyRef.current = true;
    setIsSaving(true);
    try {
      const res = await fnRequest();
      if (!Is_ValidResponse(res, dispatch)) return;
      if (typeof AppliedRef.current === "function") AppliedRef.current(); // even if the popup was closed meanwhile
      if (!IsMounted.current) return;
      if (Array.isArray(res.EntActive)) setarrActive(res.EntActive);
      Show_Message(dispatch, res.strMessage, "success");
      const objCur = CurrentRef.current;
      if (iReloadColId === 0 || iReloadColId === objCur.iColId) {
        setobjEdits({});
        Load_FilterData(objCur.iColId, objCur.strType, objCur.strType === "String" ? strLoadedSearch : "");
      }
    } catch (e) {
      if (IsMounted.current) Show_Message(dispatch, e.message, "error");
    } finally {
      BusyRef.current = false;
      if (IsMounted.current) setIsSaving(false);
    }
  };
  const Update_Filter = () => {
    const strError = Validate();
    if (strError) {
      Show_Message(dispatch, strError, "info");
      return;
    }
    const arrChecked = [];
    const arrUnChecked = [];
    if (strFieldType === "String")
      Object.keys(objEdits).forEach((k) => (objEdits[k] ? arrChecked : arrUnChecked).push(Number(k)));
    const objFilter = {
      strDataType: strFieldType,
      RptId: Number(props.iRptId),
      RptColId: Number(iRptColId),
      MSId: strFieldType === "decimal" ? Number(MathSymbol) : 0,
      MinValue: strFieldType === "decimal" ? Number(strMinValue) : 0,
      MaxValue: strFieldType === "decimal" && isMinMaxVal ? Number(strMaxValue) : 0,
      FromDate: strFieldType === "DateTime" ? strFromDate : "",
      ToDate: strFieldType === "DateTime" ? strToDate : "",
      strDBName: _AuthParems.DB,
      iBranchId: BranchId,
      strSearch: "",
      // only the values the user changed; the server merges them with the saved exclusions
      strCheckedIds: arrChecked.join(","),
      strUnCheckedIds: arrUnChecked.join(","),
    };
    Run_Change(() => API_POST(`Reports/Update_ReportFilter${UserQs}`, objFilter, dispatch), Number(iRptColId));
  };
  const Delete_ReportFilter = () => {
    Run_Change(
      () =>
        API_POST(
          `Reports/Delete_ReportFilter/${Number(props.iRptId)}/${Number(iRptColId)}/${BranchId}/${_AuthParems.DB}${UserQs}`,
          {},
          dispatch
        ),
      0
    );
  };
  const Reset_Column = () => {
    if (!Number(iRptColId)) return;
    Run_Change(
      () => API_POST(`Reports/Reset_ReportFilter/${Number(props.iRptId)}/${Number(iRptColId)}/${_AuthParems.DB}${UserQs}`, {}, dispatch),
      Number(iRptColId)
    );
  };
  const Clear_Filter = () => {
    // discard unsaved edits of the current column
    clearTimeout(SearchTimer.current);
    if (strFieldType === "String") {
      setobjEdits({});
      if (strSearch !== strLoadedSearch || strLoadedSearch) {
        setstrSearch("");
        Load_FilterData(iRptColId, strFieldType, "");
      }
    } else if (objSaved) {
      setstrFromDate(objSaved.strFrom || "");
      setstrToDate(objSaved.strTo || "");
      setMathSymbol(objSaved.iMSId || 1);
      setisMinMaxVal(objSaved.iMSId === BETWEEN_ID);
      setstrMinValue(objSaved.strMin || "");
      setstrMaxValue(objSaved.strMax || "");
    }
  };
  //#endregion

  //#region Inputs
  const Search_Values = (val) => {
    setstrSearch(val);
    clearTimeout(SearchTimer.current);
    if (strFieldType !== "String" || !Number(iRptColId)) return;
    const iColId = iRptColId;
    SearchTimer.current = setTimeout(() => Load_FilterData(iColId, "String", val.trim()), SEARCH_DELAY);
  };
  const MathSymbolChange = (MathSymble) => {
    setMathSymbol(Number(MathSymble));
    setisMinMaxVal(Number(MathSymble) === BETWEEN_ID);
  };
  const table_Onchange = (dt, val) => Set_Checked([dt], () => val);
  const Check_All = (val) => Set_Checked(arrRptData, () => val);
  const IsAllChecked = arrRptData.length > 0 && iCheckedCount === arrRptData.length;
  const arrPresets = useMemo(() => Date_Presets(props.strFinFromDate), [props.strFinFromDate]);
  const objPreset = arrPresets.find((p) => p.range && p.range[0] === strFromDate && p.range[1] === strToDate);
  const Preset_Change = (strKey) => {
    const objP = arrPresets.find((p) => p.key === strKey);
    if (!objP || !objP.range) return; // Custom Range: keep the typed dates
    setstrFromDate(objP.range[0]);
    setstrToDate(objP.range[1]);
  };
  //#endregion

  // column dropdown shows which columns already have a saved filter
  const arrColOption = useMemo(() => {
    const objOn = {};
    arrActive.forEach((a) => {
      objOn[Number(a.iRptColId)] = true;
    });
    return arrRptCol.map((dt) => (objOn[dt.iRptColId] ? { ...dt, strAliasName: dt.strAliasName + " (filtered)" } : dt));
  }, [arrRptCol, arrActive]);
  const iActiveCount = arrActive.filter((a) => a.IsApplied !== false).length;
  const IsColFiltered = arrActive.some((a) => Number(a.iRptColId) === Number(iRptColId));
  const objColSummary = arrActive.filter((a) => Number(a.iRptColId) === Number(iRptColId)).map((a) => a.strSummary).join(", ");

  return (
    <div className="popup-container">
      <div className="popup-content">
        <div className="popup-header">
          <h5 title={IsPerUser ? "Your filters apply only to your user" : "Filters apply to every user of this report"}>
            Report Filter{iActiveCount > 0 ? ` (${iActiveCount} active)` : ""}
          </h5>
          <div className="icon-popup-btn-grp">
            <span className="icon-btn" onClick={() => CloseRef.current(false)}>
              <i className="fa-regular fa-rectangle-xmark"></i>
            </span>
          </div>
        </div>
        <div className="popup-body">
          <div className="row">
            <div className="row p-0">
              <div className="col-sm-12 col-md-6 mt-1">
                <CustomizedSelectoption
                  optionparem="isoptionShow"
                  showoption={_selecrparem.isoptionShow}
                  placeholder="Filter Column"
                  select_value={(val) => {
                    if (!val || IsSaving) return;
                    if (Number(val.iRptColId) === Number(iRptColId)) return;
                    Open_Column(val.iRptColId, val.strDataType);
                  }}
                  btnname="Filter"
                  btnshow={false}
                  show_popup={""}
                  valueonly={false}
                  defaultval={iRptColId}
                  displayName="strAliasName"
                  disvalue="iRptColId"
                  arrydata={arrColOption}
                  disabled={IsSaving}
                  EmptVal="Column"
                />
              </div>
              <div className="col-sm-12 col-md-6 mt-1">
                <div className="m-hsearch">
                  <input
                    type="search"
                    placeholder="Search Values"
                    value={strSearch}
                    disabled={!isTable || IsSaving}
                    onChange={(e) => Search_Values(e.target.value)}
                    autoFocus
                  />
                  <i
                    className="fas fa-search"
                    onClick={(e) => {
                      clearTimeout(SearchTimer.current);
                      if (isTable && Number(iRptColId)) Load_FilterData(iRptColId, strFieldType, strSearch.trim());
                    }}
                  ></i>
                </div>
              </div>
            </div>
            <p className="mt-1" style={{ opacity: 0.75 }}>
              {IsPerUser ? "Filters you save here apply only to your user." : "Filters you save here apply to every user of this report."}
            </p>
            {IsColFiltered && objColSummary && <p className="mt-1">Applied: {objColSummary}</p>}
            {isAmt && (
              <div className="row">
                <div className="col-md-6 col-sm-6 mb-1">
                  <label>{isMinMaxVal ? "Min Amount" : "Amount"}</label>
                  <input
                    type="number"
                    aria-label="Amount"
                    className="form-control"
                    placeholder="Please Enter Value"
                    step="any"
                    disabled={IsSaving}
                    onClick={(e) => e.target.select()}
                    value={strMinValue}
                    onChange={(e) => setstrMinValue(e.target.value)}
                  />
                </div>
                {isMinMaxVal && (
                  <div className="col-md-6 col-sm-6 mb-1">
                    <label>Max Amount</label>
                    <input
                      type="number"
                      aria-label="Max Amount"
                      className="form-control"
                      placeholder="Please Enter Value"
                      step="any"
                      disabled={IsSaving}
                      onClick={(e) => e.target.select()}
                      value={strMaxValue}
                      onChange={(e) => setstrMaxValue(e.target.value)}
                    />
                  </div>
                )}
                <div className="col-md-6 col-sm-12 mb-1">
                  <label>Math symbol</label>
                  <CustomizedSelectoption
                    optionparem="isoptionShow1"
                    showoption={_selecrparem.isoptionShow1}
                    placeholder="Math Symble"
                    select_value={(val) => MathSymbolChange(val)}
                    btnname="Math Symble"
                    btnshow={false}
                    show_popup={""}
                    valueonly={true}
                    defaultval={MathSymbol}
                    displayName="symbol"
                    disvalue="msId"
                    arrydata={arrMathSymbol}
                    disabled={IsSaving}
                    EmptVal="Math Symble"
                  />
                </div>
              </div>
            )}
            {isDate && (
              <div className="row">
                <div className="col-md-12 col-sm-12 mt-1">
                  <label>Date Range</label>
                  <CustomizedSelectoption
                    optionparem="isoptionShow1"
                    showoption={_selecrparem.isoptionShow1}
                    placeholder="Date Range"
                    select_value={(val) => Preset_Change(val)}
                    btnname="Date Range"
                    btnshow={false}
                    show_popup={""}
                    valueonly={true}
                    defaultval={objPreset ? objPreset.key : "custom"}
                    displayName="label"
                    disvalue="key"
                    arrydata={arrPresets}
                    disabled={IsSaving}
                    EmptVal="Date Range"
                  />
                </div>
                <div className="col-md-6 col-sm-6 mt-1">
                  <label>From</label>
                  <input
                    type="Date"
                    aria-label="From Date"
                    className="form-control"
                    value={strFromDate}
                    max={strToDate || undefined}
                    disabled={IsSaving}
                    onChange={(e) => setstrFromDate(e.target.value)}
                  />
                </div>
                <div className="col-md-6 col-sm-6 mt-1">
                  <label>To</label>
                  <input
                    type="Date"
                    aria-label="To Date"
                    className="form-control"
                    value={strToDate}
                    min={strFromDate || undefined}
                    disabled={IsSaving}
                    onChange={(e) => setstrToDate(e.target.value)}
                  />
                </div>
              </div>
            )}
            {strValidation && strValidation !== "No values were changed" && (
              <p className="mt-1" role="alert" style={{ color: "red" }}>
                {strValidation}
              </p>
            )}
            {isTable && IsLoading && arrRptData.length === 0 && <p className="mt-1">Loading values...</p>}
            {isTable && !IsLoading && arrRptData.length === 0 && (
              <p className="mt-1">No values found{strLoadedSearch ? " for this search" : ""}.</p>
            )}
            {isTable && arrRptData.length > 0 && (
              <p className="mt-1">
                {iCheckedCount} of {arrRptData.length} selected
                {strLoadedSearch ? " (search results)" : ""}
                {iEditCount > 0 ? ` | ${iEditCount} unsaved change${iEditCount === 1 ? "" : "s"}` : ""}
              </p>
            )}
            {isTable && arrRptData.length > 0 && (
              <table className="mt-1">
                <thead>
                  <tr>
                    {arrFiltCol.map((colname, Index) =>
                      colname !== "Check" ? (
                        <th key={Index}>{colname}</th>
                      ) : (
                        <th key={Index} style={{ textAlign: "center" }}>
                          <input
                            type="checkbox"
                            className="form-check"
                            title="Select all"
                            checked={IsAllChecked}
                            disabled={IsSaving}
                            onChange={(e) => Check_All(e.target.checked)}
                          />
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {arrRptData.map((dt, trIndex) => (
                    <tr key={dt.Id !== undefined && !isNaN(dt.Id) ? dt.Id : trIndex}>
                      {arrFiltCol.map((colname, tdIndex) =>
                        colname !== "Check" ? (
                          <td key={tdIndex}>{dt[colname]}</td>
                        ) : (
                          <td
                            key={tdIndex}
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <input
                              type="checkbox"
                              className="form-check"
                              checked={Is_Checked(dt)}
                              disabled={IsSaving}
                              onChange={(e) => table_Onchange(dt, e.target.checked)}
                            />
                          </td>
                        )
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className="popup-footer">
          <div className="popup-btn-group">
            <button
              className="btn-fabgreen"
              disabled={IsSaving || IsLoading || !Number(iRptColId) || !IsDirty || Boolean(strValidation)}
              onClick={(e) => Ask_Question(dispatch, Update_Filter, "Do You Want to Create Filter?")}
            >
              Save&nbsp;<i className="fas fa-save"></i>
            </button>
            {IsColFiltered && (
              <button
                className="btn-fabgreen"
                disabled={IsSaving}
                title="Remove the filter of this column only"
                onClick={(e) => Ask_Question(dispatch, Reset_Column, "Do You Want to Remove This Column Filter?")}
              >
                Reset&nbsp;<i className="fa-solid fa-rotate-left"></i>
              </button>
            )}
            <button
              className="btn-fabgreen"
              disabled={IsSaving || arrActive.length === 0}
              onClick={(e) => {
                Ask_Question(dispatch, Delete_ReportFilter, "Do You Want to Delete All Filter?");
              }}
            >
              Delete&nbsp;<i className="bx bx-trash-alt"></i>
            </button>
            <button className="btn-fabgreen " disabled={IsSaving || (!IsDirty && !strSearch && !strLoadedSearch)} onClick={Clear_Filter}>
              Clear&nbsp;<i className="fa-solid fa-broom"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
export default ReportFilter;